import { gsap } from 'gsap';
import { WbTrapFocus } from './WbTrapFocus';
import { throttle } from 'lodash';

export class WbMasthead {
  constructor() {
    this.lastScroll = 0;
    this.WbTrapFocus = new WbTrapFocus({
      ele: document.querySelector('#masthead'),
      activeClass: 'wb-header--open',
    });
    this.init();
  }

  init() {
    return new Promise((resolve, reject) => {
      this.resize(true);
      this.bindListeners();

      resolve();
    });
  }

  bindListeners() {
    this.addScrollListener();
    this.addBurgerListener();
    this.addSubmenuListener();
    this.addResizeListener();
  }

  addSubmenuListener() {
    const _this = this;
    document
      .querySelector('body')
      ._addCustomEventListener('click', '.sub-menu__trigger', _this.toggleSubMenu.bind(_this));
  }

  addResizeListener() {
    const _this = this;
    window.addEventListener('resize', throttle(_this.resize.bind(_this), 200));
  }

  addBurgerListener() {
    const _this = this;
    document.querySelector('body')._addCustomEventListener('click', '#wb-burger', _this.toggleMenu.bind(_this));
  }

  addScrollListener() {
    const _this = this;

    document.addEventListener('scroll', (e) => {
      const headerAlert = document.querySelector('#wb-alert');
      let headerAlertHeight = 0;

      if (headerAlert) headerAlertHeight = headerAlert.clientHeight;

      _this.lastScroll = _this.currentScroll;
      _this.currentScroll = window.scrollY;

      if (document.querySelector('.wb-header--open')) return false;

      // Prevent errors
      // TODO : Uncomment this line if there are issues with an Apple magic mouse
      // if (Math.abs(_this.currentScroll - _this.lastScroll) < 2) return false;

      // Scrolling down
      if (_this.currentScroll > _this.lastScroll && _this.currentScroll > 0) {
        // Fix element if we scroll past its height
        if (
          _this.currentScroll > _this.eleHeight + headerAlertHeight &&
          !_this.ele.classList.contains('wb-header--fixed')
        ) {
          _this.ele.classList.add('wb-header--fixed', 'wb-header--hidden');
          gsap.to(_this.ele, { y: -_this.eleHeight, marginTop: 0, duration: 0 });
          _this.closeAllTopLevelElements(false, 0);
        }
        // Hide element while scrolling down
        else if (
          !_this.ele.classList.contains('wb-header--hidden') &&
          _this.ele.classList.contains('wb-header--fixed')
        ) {
          _this.ele.classList.add('wb-header--hidden');
          gsap.to(_this.ele, { y: -_this.eleHeight, duration: 0.15 });
          _this.closeAllTopLevelElements(false, 0);
        }
      }
      // Scrolling up
      else {
        // Show element while scrolling up
        if (_this.ele.classList.contains('wb-header--hidden')) {
          _this.ele.classList.remove('wb-header--hidden');
          gsap.to(_this.ele, { y: 0, duration: 0.15 });
        }

        // Release when we reach the top of the page
        if (_this.currentScroll <= 2 || _this.currentScroll <= headerAlertHeight) {
          gsap.to(_this.ele, { marginTop: headerAlertHeight, duration: 0 });
          _this.ele.classList.remove('wb-header--fixed');
        }
      }
    });

    document.dispatchEvent(new CustomEvent('scroll'));
    _this.setInitialScrollState();
  }

  setInitialScrollState() {
    const _this = this;
    const headerAlert = document.querySelector('#wb-alert');
    let headerAlertHeight = 0;

    if (headerAlert) headerAlertHeight = headerAlert.clientHeight;

    if (
      _this.currentScroll > _this.eleHeight + headerAlertHeight &&
      !_this.ele.classList.contains('wb-header--fixed')
    ) {
      _this.ele.classList.add('wb-header--fixed', 'wb-header--hidden');
      gsap.to(_this.ele, { marginTop: 0, duration: 0 });
    }
  }

  toggleMenu() {
    return new Promise((resolve, reject) => {
      const burger = document.querySelector('#wb-burger');
      const menus = document.querySelector('.wb-header__btm');

      if (burger.classList.contains('is-animating')) {
        reject('Target is animating.');
        return false;
      }

      const headerIsReleased = !document.querySelector('.wb-header--fixed');
      const headerIsOpen = document.querySelector('.wb-header--open');

      burger._toggleAriaExpanded();
      burger.classList.add('is-animating');
      this.ele.classList.add('wb-header--open');

      document.querySelectorAll('html, body').forEach((e) => e.classList.add('--locked'));

      menus
        ._slideToggle()
        .then(() => {
          burger.classList.remove('is-animating');

          if (headerIsOpen) {
            document.querySelectorAll('html, body').forEach((e) => e.classList.remove('--locked'));
            this.ele.classList.remove('wb-header--open');
          }

          if (headerIsReleased && headerIsOpen) this.toggleAlert();
        })
        .then(resolve);
    });
  }

  toggleSubMenu(e) {
    return new Promise((resolve, reject) => {
      const ele = e.targetElement;
      const parent = ele.closest('.wb-menu__item');
      const submenu = ele.nextSibling;

      this.closeAllTopLevelElements(ele);

      ele._toggleAriaExpanded();
      parent._toggleAriaExpanded();
      submenu._slideToggle().then(resolve);
      this.fixSubmenus();
    });
  }

  fixSubmenus() {
    const _this = this;

    document.querySelectorAll('.wb-menu--main .level-0')?.forEach((e) => {
      const submenu = e.querySelector('.sub-menu');

      if (submenu) {
        const menuPadding = parseFloat(
          window.getComputedStyle(_this.ele.querySelector('.container-fluid')).paddingLeft,
        );

        const menuWidth = _this.ele.clientWidth - parseFloat(menuPadding) * 2;
        const menuItemLeftPosition = e.offsetLeft;
        const submenuWidth = submenu.clientWidth / 2;
        const submenuFitsInContainer = menuItemLeftPosition - menuPadding + submenuWidth < menuWidth;

        if (submenuFitsInContainer) submenu.classList.remove('sub-menu--right');
        else submenu.classList.add('sub-menu--right');
      }
    });
  }

  closeAllTopLevelElements(ele, duration = 0.35) {
    const parent = ele?.closest?.('.wb-menu__item') || false;
    const level = ele ? parent.getAttribute('data-level') : 0;
    const parentNav = ele?.closest?.('nav') || this.ele;

    if (level == 0) {
      parentNav.querySelectorAll('.level-0[aria-expanded="true"] > .sub-menu').forEach((e) => {
        if (e.closest('.level-0') != parent) {
          e._slideUp(duration);
          e.previousSibling.setAttribute('aria-expanded', 'false');
          e.closest('.level-0').setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  closeMenu() {
    if (document.querySelector('.wb-header--open')) {
      document.querySelector('#wb-burger').dispatchEvent(new Event('click'));
    }

    this.closeAllTopLevelElements(false, 0);
  }

  toggleAlert() {
    const alert = document.querySelector('#wb-alert');
    const alertIsVisible = alert && alert.style.display !== 'none' && !document.querySelector('.wb-header--fixed');

    if (alertIsVisible) {
      const marginTop = document.querySelector('.wb-header--open') ? 0 : alert.clientHeight;
      gsap.to(this.ele, { marginTop, duration: 0.15 });
    }
  }

  setAlertOffset() {
    const alert = document.querySelector('#wb-alert');
    const alertIsVisible = alert && alert.style.display !== 'none' && !document.querySelector('.wb-header--fixed');

    if (alertIsVisible) this.ele.style.marginTop = `${alert.clientHeight}px`;
    else this.ele.style.marginTop = 0;
  }

  setHeaderOffset() {
    const masthead = document.querySelector('#masthead');
    let ref = document.querySelector('.wb-banner') || document.querySelector('main');

    if (masthead.classList.contains('wb-header--bg')) ref = document.querySelector('main');

    ref.style.paddingTop = '';

    const headerHeight = masthead.clientHeight;
    const basePadding = parseFloat(window.getComputedStyle(ref).getPropertyValue('padding-top'));
    ref.style.paddingTop = `${basePadding + headerHeight}px`;
  }

  resize(force = false) {
    const _this = this;
    this.ele = document.querySelector('#masthead');

    if (this.vw != this.oldVw || force) {
      this.oldVw = this.vw;
      this.vw = window.innerWidth;
      this.vh = window.innerHeight;
      this.eleHeight = this.ele.clientHeight;

      this.closeMenu();
      this.setAlertOffset();
      this.setHeaderOffset();
    }
  }
}
